import axiosLib from "axios";
import custom from "../utils/custom.js";
import { notification } from "antd";
import { getStore, setStore } from "../utils/global.js";
import logger from "../utils/logger.js";

const origin = getStore("origin");
let baseUrl = `${origin}/`;

if (process.env.NODE_ENV === "development") {
  baseUrl = "https://127.0.0.1:8000/";
}

const _axios = axiosLib.create({
  baseURL: baseUrl,
  timeout: 180000,
});

_axios.interceptors.request.use(async (config) => {
  const lang = window.localStorage.getItem("vsign_lang") || "en";
  config.headers["Accept-Language"] = lang;
  const accessToken = custom.getCookie("token");
  if (accessToken) config.headers["Authorization"] = accessToken;
  return config;
});

_axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error.response?.status === 401) {
      // Initial load skips error notification.
      const user = getStore("user");
      if (user.detail !== "init") {
        notification.error({
          message: "Error acquired. ",
          description: error.response?.data?.message,
          duration: 2,
        });
        custom.removeCookie("token");
        setStore("user", "init");
      }
      custom.setCookie("redirect_url", window.location.hash);
      if (custom.getUrlParameter("msisdn")) {
        window.location.hash = "#/login/mobile?msisdn=" + custom.getUrlParameter("msisdn");
      } else {
        if (custom.getUrlParameter("email")) {
          window.location.hash = "#/login/email?email=" + custom.getUrlParameter("email");
        } else {
          window.location.hash = "#/login/mobile";
        }
      }
      return Promise.reject({
        data: { message: error.response?.data?.message },
      });
    }
    if (error.response?.status === 500) {
      return Promise.reject({
        data: { message: "System error acquired. " },
      });
    }
    return Promise.reject(error.response);
  },
);

const handleErrorMessage = (error) => {
  let errorMessage = "";
  let description = "Хүсэлт амжилтгүй боллоо";
  const { data } = error;

  const { message, title, detail, error_message } = data;

  if (typeof message === "string") {
    description = message;
  } else {
    if (message?.message) {
      description = message?.message;
    } else if (data.validation && typeof data.validation === "object" && Object.keys(data.validation).length > 0) {
      let messages = [];

      for (const key in data.validation) {
        const validationMessages = data.validation[key] || [];
        messages = [...messages, ...validationMessages];
      }

      if (messages.length > 0) {
        description = messages.join(", ");
      }
    }
  }

  if (typeof title === "string") {
    errorMessage = title;
    if (typeof detail === "string") {
      description = detail;
    }
  }
  if (typeof error_message === "string") {
    description = error_message;
  }

  return { errorMessage, description };
};

const request = async (
  service,
  data,
  {
    formData = false,
    successMessage = "",
    showSuccessMessage = false,
    showErrorMessage = false,
    errorMessage = "",
    customHeader = {},
    download = false,
    fileName = "",
  } = {},
  method = "post",
) => {
  let responseData;
  try {
    let body;
    if (formData) {
      body = data;
    } else if (data) {
      body = data;
    }

    logger.debug(service + " body: ", body);

    let headers = {
      "Content-Type": "application/json",
    };

    if (customHeader && typeof customHeader === "object" && Object.keys(customHeader).length) {
      headers = {
        ...headers,
        ...customHeader,
      };
    }

    if (formData) headers["Content-Type"] = "multipart/form-data";

    const config = { headers };
    if (download) config.responseType = "blob";

    const response = await _axios[method](service, body, config);

    if (!response.data) {
      notification.error({ message: "Internal server error", duration: 2 });
      return { success: false, data: response };
    }

    if (download) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || "download");
      document.body.appendChild(link);
      link.download = fileName || response.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || "download";
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { success: true, data: null, message: "File downloaded" };
    }

    responseData = response.data;

    const message = responseData.message || (responseData.data && responseData.data.message) || successMessage || "";

    if (showSuccessMessage) {
      notification.success({ message: "Амжилттай", description: message || successMessage || "", duration: 2 });
    }

    logger.debug(service, " response: ", message, responseData);

    return {
      success: true,
      data: responseData,
      message,
    };
  } catch (e) {
    logger.error(service + " error: ", e);
    let message = "Амжилтгүй";
    let desc = "Хүсэлт амжилтгүй боллоо";
    if (responseData) {
      const { errorMessage, description } = handleErrorMessage({ data: responseData });
      if (errorMessage) message = errorMessage;
      desc = description;
    }
    if (e?.data) {
      const { errorMessage, description } = handleErrorMessage({ data: e.data });
      if (errorMessage) message = errorMessage;
      desc = description;
    }
    if (e?.response) {
      const { errorMessage, description } = handleErrorMessage(e.response);
      if (errorMessage) message = errorMessage;
      desc = description;
    }

    if (showErrorMessage) {
      notification.error({
        message: message,
        description: desc || errorMessage || "",
        duration: 2,
      });
    }

    return {
      success: false,
      data: null,
      message,
    };
  }
};

const patch = (service, data, options) => request(service, data, options, "patch");
const put = (service, data, options) => request(service, data, options, "put");
const post = (service, data, options) => request(service, data, options, "post");
const _delete = (service, data, options) => request(service, data, options, "delete");

const get = async (
  service,
  params,
  { successMessage = "", showSuccessMessage = false, showErrorMessage = false, errorMessage = "", customHeader = {} } = {},
) => {
  try {
    const headers = {
      ...customHeader,
    };

    const response = await _axios.get(service, { headers, params });

    if (!response.data) {
      return { success: false, data: response };
    }

    const responseData = response.data;

    const message = responseData.message || (responseData.data && responseData.data.message) || successMessage || "";

    if (showSuccessMessage) {
      notification.success({
        message: "Амжилттай",
        description: message || successMessage || "",
        duration: 2,
      });
    }

    return {
      success: true,
      data: responseData,
      message,
    };
  } catch (e) {
    logger.error(service + " error: ", e?.response?.data);
    let message = "Амжилтгүй";
    let desc = "Хүсэлт амжилтгүй боллоо";
    if (e.response) {
      const { errorMessage, description } = handleErrorMessage(e.response);
      if (errorMessage) message = errorMessage;
      desc = description;
    }

    if (showErrorMessage) {
      notification.error({
        message: message,
        description: desc || errorMessage || "",
        duration: 2,
      });
    }

    return {
      success: false,
      data: null,
      message,
    };
  }
};

const set_request_header = (key, value) => {
  _axios.defaults.headers.common[key] = value;
};

const axios = { get, post, patch, put, delete: _delete, set_request_header };

export default axios;
